import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { TableCell } from 'common/components/Table';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';
import {
    IBasicUserFragment,
    IContentTypeValue,
    IPortfolioItemChildFragment,
    ISkillInputOptionFragment,
} from 'graphql/types';
import { PortfolioItemProgressScoreChip } from 'user/components/PortfolioItemProgressScoreChip';
import { ReceivedCertificateProgressChip } from 'common/components/ReceivedCertificateProgressChip';
import { CERTIFICATE_COLUMN_STAGGER_MS } from 'skill/constants/skillReportMatrix';
import { ISkillCertificateColumn } from 'skill/hooks/useSkillReportColumnExpansion';

interface IProps {
    certificate?: ISkillCertificateColumn;
    child?: IPortfolioItemChildFragment;
    user: IBasicUserFragment;
    skill?: ISkillInputOptionFragment;
    isExpanded: boolean;
    isLast: boolean;
    isLoading?: boolean;
    columnIndex?: number;
    onUpdate?(): void;
}

export const SkillReportMatrixCertificateBodyCell = ({
    certificate,
    child,
    user,
    skill,
    isExpanded,
    isLast,
    isLoading,
    columnIndex = 0,
    onUpdate,
}: IProps) => {
    const { openGlobalDrawer } = useGlobalDrawer();
    const [isMounted, setIsMounted] = useState(false);

    // Draw the cell closed first, then open it on the next frame. That tiny
    // delay is what lets the CSS transition actually animate — without it
    // the cell would just appear fully open with no slide-in.
    useEffect(() => {
        const id = requestAnimationFrame(() => setIsMounted(true));

        return () => cancelAnimationFrame(id);
    }, []);

    const visible = isMounted && isExpanded;
    // Stagger expand to ripple columns left-to-right; collapse with no delay
    // so it stays snappy.
    const transitionDelay = visible
        ? `${columnIndex * CERTIFICATE_COLUMN_STAGGER_MS}ms`
        : '0ms';

    const isReceivedCertificate =
        child?.relatedType === IContentTypeValue.ReceivedCertificate;
    const validationStatus = child?.metadata?.validationStatus ?? undefined;
    const isMandatory = !!child?.metadata?.isMandatory;

    const handleClick = useCallback(() => {
        if (!certificate) return;

        openGlobalDrawer({
            type: IGlobalDrawerType.ReceivedCertificate,
            itemId: child?.id,
            props: {
                canGenerateCertificate: true,
                title: child?.title ?? certificate.name,
                user,
                skill,
                certificateId: isReceivedCertificate
                    ? (child?.metadata?.certificateId ?? certificate.id)
                    : (child?.relatedId ?? certificate.id),
                isExternalCertificate: child
                    ? child.metadata?.isExternalCertificate
                    : certificate.isExternalCertificate,
                requiresValidation: child?.metadata?.requiresValidation,
                onDeleteCertificate: () => {
                    onUpdate?.();
                },
                onGenerateCertificate: () => {
                    onUpdate?.();
                },
                onSetExternalStatus: () => {
                    onUpdate?.();
                },
            },
        });
    }, [
        certificate,
        child,
        isReceivedCertificate,
        openGlobalDrawer,
        onUpdate,
        user,
        skill,
    ]);

    return (
        <TableCell
            sx={{
                p: 0,
                width: visible ? 44 : 0,
                minWidth: visible ? 44 : 0,
                maxWidth: visible ? 44 : 0,
                bgcolor: visible ? 'grey.100' : 'transparent',
                borderRightStyle: 'solid',
                borderRightWidth: visible && isLast ? '1px' : 0,
                borderRightColor: 'divider',
                borderBottomColor: visible ? 'divider' : 'transparent',
                cursor: visible && certificate ? 'pointer' : undefined,
                transition:
                    'width 0.3s ease, min-width 0.3s ease, max-width 0.3s ease, background-color 0.3s ease, border-right-width 0.3s ease, border-color 0.3s ease',
                transitionDelay,
            }}
            onClick={visible && certificate ? handleClick : undefined}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    minWidth: visible ? 44 : 0,
                    transition: 'min-width 0.3s ease',
                    transitionDelay,
                }}
            >
                <Box
                    sx={{
                        overflow: 'hidden',
                        flexShrink: 0,
                        width: visible ? 44 : 0,
                        opacity: visible ? 1 : 0,
                        transition: 'width 0.3s ease, opacity 0.3s ease',
                        transitionDelay,
                    }}
                >
                    {certificate && child ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 1,
                            }}
                        >
                            <PortfolioItemProgressScoreChip
                                withIcon
                                isMandatory={isMandatory}
                                portfolioItem={child}
                                receivedCertificateValidationStatus={
                                    validationStatus
                                }
                            />
                        </Box>
                    ) : isLoading ? (
                        // Per-user data still in flight — show a spinner
                        // rather than the empty "no portfolio item" chip,
                        // which would flash through on the way to real data
                        // after pagination.
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 1,
                            }}
                        >
                            <CircularProgress size={28} />
                        </Box>
                    ) : certificate ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 1,
                            }}
                        >
                            <ReceivedCertificateProgressChip
                                withIcon
                                isMandatory={certificate.isMandatory}
                                sx={{
                                    '.MuiChip-icon': {
                                        mx: 'auto',
                                        color: 'common.white',
                                        fontSize: 16,
                                    },
                                    '.MuiChip-label': { display: 'none' },
                                }}
                            />
                        </Box>
                    ) : null}
                </Box>
            </Box>
        </TableCell>
    );
};
